import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  Truck, ClipboardList, FileText, LayoutDashboard,
  Users, DollarSign, CreditCard, TrendingUp, Settings,
  LogOut, Menu, X, ChevronRight
} from 'lucide-react';

export default function NavBar() {
  const { isAdmin, logout, profile, darkMode, toggleDarkMode } = useApp();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const driverLinks = [
    { to: '/log', label: 'Log Entry', icon: <ClipboardList size={20} /> },
    { to: '/logs', label: 'My Logs', icon: <FileText size={20} /> },
  ];

  const adminLinks = [
    { to: '/admin', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { to: '/log', label: 'Log Entry', icon: <ClipboardList size={20} /> },
    { to: '/logs', label: 'My Logs', icon: <FileText size={20} /> },
  ];

  const links = isAdmin ? adminLinks : driverLinks;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/');
  };

  const isActive = (path) => loc.pathname === path;

  return (
    <>
      {/* Top bar */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center',
        padding: 'env(safe-area-inset-top) 16px 0',
        minHeight: 'calc(56px + env(safe-area-inset-top))',
        zIndex: 200,
        gap: 12,
      }}>
        {/* Hamburger */}
        <button
          onClick={() => setOpen(true)}
          style={{
            background: 'none', border: 'none', color: 'var(--text)',
            padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center',
            marginLeft: -8,
          }}
        >
          <Menu size={24} />
        </button>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Truck size={18} color="var(--accent)" />
          <span style={{
            fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 16,
            letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text)',
          }}>
            Daily Driver Pulse
          </span>
        </div>

        {/* Current page indicator on mobile */}
        <div style={{
          fontFamily: 'Barlow Condensed', fontWeight: 600, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)',
        }}>
          {links.find(l => isActive(l.to))?.label || (isAdmin ? 'Admin' : '')}
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div style={{ height: 'calc(56px + env(safe-area-inset-top))' }} />

      {/* Overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            zIndex: 300, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Side Drawer */}
      <div style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: 280, background: 'var(--bg2)',
        borderRight: '1px solid var(--border)',
        zIndex: 400,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
        paddingTop: 'env(safe-area-inset-top)',
      }}>

        {/* Drawer header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          minHeight: 56,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truck size={20} color="var(--accent)" />
            <span style={{
              fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 15,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>Daily Driver Pulse</span>
          </div>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* User info */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg3)',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--accent)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', marginBottom: 10,
            fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 18, color: '#fff',
          }}>
            {(profile?.name || 'U')[0].toUpperCase()}
          </div>
          <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>
            {profile?.name || 'User'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {profile?.companyName || ''}
          </div>
          <div style={{ marginTop: 6 }}>
            <span style={{
              fontSize: 10, fontFamily: 'Barlow Condensed', fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '2px 8px', borderRadius: 2,
              background: isAdmin ? 'var(--accent-dim)' : 'var(--bg)',
              color: isAdmin ? 'var(--accent)' : 'var(--text-dim)',
            }}>
              {isAdmin ? 'Admin' : 'Driver'}
            </span>
          </div>
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 20px',
                textDecoration: 'none',
                color: isActive(link.to) ? 'var(--accent)' : 'var(--text-muted)',
                background: isActive(link.to) ? 'var(--bg3)' : 'transparent',
                borderLeft: isActive(link.to) ? '3px solid var(--accent)' : '3px solid transparent',
                transition: 'all 0.15s',
                fontSize: 15,
                fontFamily: 'Barlow Condensed',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {link.icon}
              <span style={{ flex: 1 }}>{link.label}</span>
              {isActive(link.to) && <ChevronRight size={14} />}
            </Link>
          ))}

          {/* Dark mode toggle */}
          <div style={{ margin: '8px 0', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <button
              onClick={toggleDarkMode}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 20px', width: '100%',
                background: 'none', border: 'none',
                color: 'var(--text-muted)', textAlign: 'left',
                fontFamily: 'Barlow Condensed', fontWeight: 600,
                fontSize: 15, letterSpacing: '0.04em', textTransform: 'uppercase',
                borderLeft: '3px solid transparent',
              }}
            >
              <span style={{ fontSize: 20 }}>{darkMode ? '☀️' : '🌙'}</span>
              <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
          </div>
        </nav>

        {/* Logout */}
        <div style={{ padding: '8px 0 24px', borderTop: '1px solid var(--border)' }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 20px', width: '100%',
              background: 'none', border: 'none',
              color: 'var(--danger)', textAlign: 'left',
              fontFamily: 'Barlow Condensed', fontWeight: 600,
              fontSize: 15, letterSpacing: '0.04em', textTransform: 'uppercase',
              borderLeft: '3px solid transparent',
            }}
          >
            <LogOut size={20} />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    </>
  );
}
